import type { Glyph } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { domeLight, type Ellipse, type HeadPicture, inEllipse, mask, type SetFlair } from './mythic';

/**
 * The Ooze set (ADR-0160 amended, mythic): a living blob of goo in a gardener's shape. A wobbling dome of magenta jelly
 * for a head, a suit half dissolved in goo, the Gloop Gun.
 *
 * The head is a mask over the whole head: a dome of see-through magenta goo (lit pink on the top-left, plum where it
 * turns away, a band of light caught through the jelly along its bottom-right rim, a white gleam), goo dripping from its
 * underside in three drops; inside it float a nucleus in the team's colour (its heart lit), two round white eyes and a
 * few bubbles. Lying, its eyes are crosses. Its flair: a dollop of goo on its crown jiggling side to side and squashing
 * (a `glyph`: the bright moving part at 1:1), bubbles rising inside the dome, drops falling from its underside.
 * The suit is a jumpsuit in the team's colour with goo poured over its shoulders, running down it in drips of every
 * length, a slate belt, its hem melting into goo and goo boots. Its flair: drips falling off it, two strands of goo hanging
 * from it that swing and shed drops, puddles left in its footprints on the run.
 *
 * Rubric (`assetc mythic ooze` at 5× and 1:1 on grass, lane and night, both teams, idle, run and dead, beside Disco,
 * Origami and Inferno): head S4 R5 P4 L4 C4 A4 F4, suit S4 R4 P4 L4 C4 F4.
 */

// --- The dome of goo -------------------------------------------------------------------------------------------------

/** The dome (u forward, v down from the head's middle), its drops hanging from its underside: [u, length, width]. */
const DOME: Ellipse = [1, -3, 21.5, 19.5];
const DROPS = [
  [-11, 5, 2.2],
  [3, 7.5, 2.6],
  [14.5, 4, 2],
] as const;
/** The nucleus in the team's colour, floating up behind the eyes. */
const CORE: Ellipse = [-6, -10, 4.6, 4];
/** Its lobes, so it reads as a cell's nucleus, not an eye. */
const LOBES: readonly Ellipse[] = [CORE, [-9.5, -6.5, 3, 2.8], [-2.5, -6.5, 2.4, 2.2]];
/** The eyes: round, white, their pupils looking forward. */
const EYES = [
  [7, 0, 4.3],
  [16.5, -1, 3.6],
] as const;
/** Bubbles caught in it: [u, v, r]. */
const BUBBLES = [
  [-13, 4, 1.7],
  [-1, 9, 1.2],
  [8, -13, 1.5],
  [-16, -6, 1],
] as const;

/** The dome's edge, wobbling a little round it (the goo is never quite round). */
const domeAt = (u: number, v: number): number => {
  const a = Math.atan2(v - DOME[1], u - DOME[0]);
  return inEllipse(u, v, DOME) * (1 + Math.sin(a * 5 + 0.6) * 0.03);
};

/** A drop hanging under the dome at `u`: a strand narrowing, then a round drop at its end. */
function inDrop(u: number, v: number): boolean {
  for (const [du, len, w] of DROPS) {
    const bottom = DOME[1] + DOME[3] * Math.sqrt(Math.max(0, 1 - ((du - DOME[0]) / DOME[2]) ** 2));
    const end = bottom + len;
    if (Math.hypot(u - du, v - end) < w) return true;
    if (v > bottom - 2 && v < end && Math.abs(u - du) < w * (0.55 + 0.45 * (1 - (v - bottom) / len))) return true;
  }
  return false;
}

const goo: HeadPicture = (u, v, lx, ly, lying) => {
  const e = domeAt(u, v);
  const drop = !lying && inDrop(u, v);
  if (e > 1 && !drop) return null;
  // The eyes: white rounds, a near-black pupil forward (crosses when it lies).
  for (const [eu, ev, r] of EYES) {
    const [du, dv] = [u - eu, v - ev];
    const d = Math.hypot(du, dv);
    if (d > r) continue;
    if (lying) return Math.abs(Math.abs(du) - Math.abs(dv)) < 0.8 && d < r - 0.6 ? 'k' : 'w';
    if (Math.hypot(du - r * 0.35, dv + 0.2) < r * 0.48) return 'k';
    return du < -r * 0.5 && dv > 0 ? '5' : 'w';
  }
  // The nucleus: the team's colour, its heart lit, its rim dark.
  const n = Math.min(...LOBES.map((e) => inEllipse(u, v, e)));
  if (n <= 1) {
    if (n > 0.55 && u - CORE[0] + (v - CORE[1]) > -3) return 'B';
    return Math.hypot(u - CORE[0] + 1.4, v - CORE[1] + 1.2) < 1.6 ? 'c' : 'b';
  }
  // A halo of its light through the goo round it.
  if (n <= 1.6) return 'i';
  for (const [bu, bv, r] of BUBBLES) {
    const d = Math.hypot(u - bu, v - bv);
    if (d < r) return u < bu && v < bv ? 'w' : 'i';
    if (d < r + 0.6) return 'i';
  }
  if (drop) return Math.abs(u - DROPS.find(([du]) => Math.abs(u - du) < 4)![0] + 0.8) < 0.7 ? 'i' : 'p';
  // The gleam: an arc of white on the top-left, a speck under it.
  const a = Math.atan2(v - DOME[1], u - DOME[0]);
  if (e > 0.48 && e < 0.68 && a < -1.65 && a > -2.75) return 'w';
  if (Math.hypot(u + 12.5, v + 6) < 1.1) return 'w';
  // Light caught through the jelly along the bottom-right rim.
  if (e > 0.8 && e < 0.9 && a > -0.3 && a < 1.9) return 'i';
  // Deep in the jelly it darkens toward its bottom-right, its lit rim on the top-left.
  const l = domeLight(u, v, DOME, lx, ly);
  if ((e > 0.82 && l > 0.55) || l > 0.93) return 'i';
  return l > 0.2 ? 'p' : 'P';
};

// --- The suit drenched in goo ----------------------------------------------------------------------------------------

/** Drips running down from the goo on the shoulders: [x, length, width]. */
const DRIPS = [
  [13, 7, 2],
  [19, 3, 1.5],
  [24, 9, 2],
  [33, 5, 1.5],
  [39, 8, 2],
  [47, 4, 2],
] as const;

/** The goo's tone at (x, y) of its run (lit on its left and top, plum on its right), a glint here and there. */
const gooTone = (x: number, y: number, left: boolean, right: boolean, top: boolean): PaletteKey =>
  top || left ? 'i' : right ? 'P' : (x * 7 + y * 3) % 23 === 0 ? 'w' : 'p';

/**
 * The suit standing (the frame's x 5-56, y 39-57): a jumpsuit in the team's colour, its shoulders round, goo poured over
 * them and running down in drips, a slate belt, the hem melting.
 */
function suit(): Part {
  const [x0, y0] = [5, 39];
  const c = new PixelCanvas(52, 19);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  const inSuit = (x: number, y: number) => {
    if (y < 41 || y > 57) return false;
    if (x >= 16 && x <= 43) return true;
    for (const [cx, rx] of [
      [12.5, 7.5],
      [47, 6.5],
    ] as const) {
      const [dx, dy] = [(x + 0.5 - cx) / rx, (y + 0.5 - 47) / 5.8];
      if (dx * dx + dy * dy <= 1) return true;
    }
    return false;
  };
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++) {
      if (!inSuit(x, y)) continue;
      const lit = !inSuit(x - 1, y) || !inSuit(x, y - 1);
      const shade = !inSuit(x + 1, y) || x > 41 || (x >= 29 && x <= 30 && y > 47);
      at(x, y, lit ? 'c' : shade ? 'B' : 'b');
    }
  // The slate belt, a goo-filled vial for its buckle.
  for (let x = 16; x <= 43; x++) {
    at(x, 53, '3');
    at(x, 54, x > 40 ? '1' : '2');
  }
  c.stamp(
    `
    3wi3
    3ip3
  `,
    27 - x0,
    53 - y0,
  );
  // Goo over the shoulders: a sheet from the collar down, wavy along its foot, and drips running from it.
  const sheet = (x: number) => 44 + Math.round(Math.sin(x * 0.9) * 0.8);
  const inGoo = (x: number, y: number): boolean => {
    if (!inSuit(x, y) && y > 43) return false;
    if (y < 41 || x < 5 || x > 56) return false;
    if (y <= sheet(x)) return inSuit(x, y) || y >= 41;
    for (const [dx, len, w] of DRIPS) {
      const end = 44 + len;
      if (Math.hypot(x + 0.5 - dx, y + 0.5 - end) < w) return true;
      if (y < end && Math.abs(x + 0.5 - dx) < w * 0.6) return true;
    }
    return false;
  };
  for (let y = 41; y <= 57; y++)
    for (let x = 5; x <= 56; x++)
      if (inSuit(x, y) && inGoo(x, y))
        at(x, y, gooTone(x, y, !inGoo(x - 1, y), !inGoo(x + 1, y) || !inGoo(x, y + 1), !inGoo(x, y - 1)));
  // The hem melting: goo pooling round the waist's foot.
  for (let x = 17; x <= 42; x++) {
    const lump = Math.sin(x * 0.7) > 0.2;
    at(x, 56, lump ? 'p' : 'b');
    at(x, 57, lump ? 'P' : 'p');
  }
  return [c, x0, y0];
}

/** The suit lying (right of the legs, under the head): its goo-dripped shoulders by the head (right), the belt (left). */
const DEAD_SUIT = grid(`
  ....cbii
  ...cbbpi
  pP2cbbpi
  pP3cbpip
  pP2cbbpP
  pP3cbbip
  pP2cbbbp
  pP3cbbpi
  pP2cbbpp
  pP3cbbbp
  pP2cbbpi
  pP3cbbip
  pP2cbbpp
  pP3cbbpi
  pP2cbbbp
  pP3cbbpi
  pP2BBBpP
  ....BBpP
`);

/** Goo boots under the team's trousers. */
const SUIT = (): Outfit => ({ torso: suit(), dead: [DEAD_SUIT, 24, 51], legs: { n: 'p', N: 'P', m: 'i' } });

// --- The Gloop Gun's goo on every other gun ----------------------------------------------------------------------------

/** The goo finish (the default gun is its own, `pollinator_ooze`): magenta goo for steel, slate for wood, plum for brass. */
const GOOED = {
  '3': 'P',
  '4': 'p',
  '5': 'i',
  N: '1',
  n: '2',
  m: '3',
  t: '4',
  U: 'P',
  Y: 'p',
  y: 'i',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

const WHITE = 0xffffff;
const PINK = 0xf6757a;
const MAGENTA = 0xb55088;
const PLUM = 0x68386c;
const DEEP = 0x3e2731;

/** The dollop's box, its base's middle. */
const DOLLOP = [22, 18] as const;
const BASE = [11, 16] as const;

/**
 * A frame of the dollop on the crown: a round lump of goo leaning `lean` px at its top and `h` px tall (squashed wider
 * as it sinks), its tip curling, rimmed in deep plum, lit pink on its top-left with a white gleam.
 */
function dollop(lean: number, h: number): string {
  const rows = Array.from({ length: DOLLOP[1] }, () => Array(DOLLOP[0]).fill('.') as string[]);
  // Its ball: squashed wider as it sinks, riding `lean` over its base; its neck a cone down to the dome.
  const [rx, ry] = [6 * Math.sqrt(8 / h), 5 * (h / 8)];
  const [bx, by] = [BASE[0] + lean, BASE[1] - h + ry * 0.6];
  const inside = (x: number, y: number): boolean => {
    if (y > BASE[1] + 0.5) return false;
    if (((x - bx) / rx) ** 2 + ((y - by) / ry) ** 2 <= 1) return true;
    if (y < by) return false;
    const t = (y - by) / (BASE[1] - by);
    return Math.abs(x - (bx + (BASE[0] - bx) * t)) < 2.2 + t * t * 4;
  };
  for (let y = 0; y < DOLLOP[1]; y++)
    for (let x = 0; x < DOLLOP[0]; x++) {
      const [px, py] = [x + 0.5, y + 0.5];
      const row = rows[y] as string[];
      if (inside(px, py)) {
        const edgeL = !inside(px - 1, py) || !inside(px, py - 1);
        const edgeR = !inside(px + 1, py) || !inside(px, py + 1);
        const gleam = Math.hypot(px - bx + 2.2, py - by + 1.8) < 1.3;
        row[x] = gleam ? 'w' : edgeL ? 'i' : edgeR && py < BASE[1] - 1 ? 'P' : 'p';
      } else if (
        py < BASE[1] - 1 &&
        (inside(px - 1, py) || inside(px + 1, py) || inside(px, py + 1) || inside(px, py - 1))
      )
        row[x] = 'o';
    }
  return rows.map((r) => r.join('')).join('\n');
}

/** Its jiggle: up tall, leaning one way, squashed, the other way, back. */
const JIGGLE = [
  dollop(0, 10),
  dollop(3, 9.5),
  dollop(5, 8),
  dollop(2, 6.5),
  dollop(-3, 8),
  dollop(-5, 9.5),
  dollop(-2, 10.5),
  dollop(0, 8.5),
];

const jiggle: Glyph = {
  kind: 'glyph',
  // The dollop's base on the dome's crown (the frame's (31, 6)).
  at: { on: 'head', x: 31 - BASE[0], y: 8 - BASE[1] },
  frames: JIGGLE,
  tones: { w: WHITE, i: PINK, p: MAGENTA, P: PLUM, o: DEEP },
  hold: [0.08, 0.08],
  order: 'cycle',
  layer: 'front',
};

/** A drop falling: pink, then magenta, then plum as it goes. */
const DRIP_TONES = [PINK, MAGENTA, MAGENTA, PLUM] as const;

/** A strand of goo hanging from the suit, swinging, a drop shed off its end now and then. */
const strand = (x: number, sway: number, phase: number, n: number) =>
  ({
    kind: 'chain',
    at: { on: 'torso', x, y: 55 },
    n,
    seg: 2.2,
    dir: Math.PI * 0.55,
    stiff: 3,
    g: 220,
    drag: 0.1,
    sway: [0.15, sway, phase],
    w: [3, 2, 2],
    shape: 'dots',
    tones: [MAGENTA, MAGENTA, PINK],
    rim: PLUM,
    lit: PINK,
    shed: {
      rate: 0.8,
      v: [-2, 2, 6, 14],
      g: 160,
      drag: 0.9,
      life: [0.3, 0.5],
      size: [2, 2],
      tall: 1.5,
      tones: DRIP_TONES,
    },
    layer: 'front',
  }) as const;

const FLAIR: SetFlair = {
  head: [
    jiggle,
    // The nucleus glowing in the team's light through the goo.
    {
      kind: 'pulse',
      at: { on: 'head', x: 24, y: 19 },
      r: [5, 9],
      alpha: [0.25, 0.5],
      period: 1.1,
      tone: 'teamLight',
      layer: 'front',
    },
    // Bubbles rising inside the dome, popping before its top.
    {
      kind: 'emit',
      at: { on: 'head', x: 30, y: 30 },
      box: [26, 10],
      rate: 4,
      v: [-1, 1, -12, -7],
      wobble: [1, 2.5],
      life: [0.9, 1.4],
      size: [2, 1],
      tones: [WHITE, PINK],
      alpha: [0.9, 0.5],
      layer: 'front',
    },
    // Drops falling from its underside.
    {
      kind: 'emit',
      at: { on: 'head', x: 32, y: 47 },
      box: [24, 2],
      rate: 1.6,
      v: [-2, 2, 4, 10],
      g: 170,
      drag: 0.9,
      life: [0.45, 0.6],
      size: [2, 2],
      tall: 1.5,
      tones: DRIP_TONES,
      layer: 'front',
    },
  ],
  outfit: [
    strand(14, 1.1, 0, 3),
    strand(44, 0.9, 0.5, 2),
    // Drips falling off the suit.
    {
      kind: 'emit',
      at: { on: 'torso', x: 30, y: 54 },
      box: [30, 4],
      rate: 1.2,
      v: [-2, 2, 6, 14],
      g: 170,
      drag: 0.9,
      life: [0.3, 0.45],
      size: [2, 2],
      tall: 1.5,
      tones: DRIP_TONES,
      layer: 'front',
    },
    // Puddles left in its footprints on the run, wobbling flat and drying.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 69 },
      box: [12, 2],
      rate: 0,
      run: 0.07,
      v: [0, 0, 0, 0],
      life: [0.8, 1.2],
      size: [5, 9],
      tall: 0.4,
      tones: [PINK, MAGENTA, PLUM],
      alpha: [0.95, 0.3],
      layer: 'back',
    },
  ],
  gun: [
    // The jar's goo glowing as it bubbles, bubbles rising out of its lid and a drop hanging off the nozzle.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 33, y: 9 },
      r: [3, 6],
      alpha: [0.2, 0.4],
      period: 0.8,
      tone: MAGENTA,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 33, y: 2 },
      box: [6, 1],
      rate: 2.2,
      v: [-2, 2, -12, -6],
      wobble: [1, 3],
      life: [0.4, 0.7],
      size: [2, 1],
      tones: [PINK, WHITE],
      alpha: [1, 0.4],
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 60, y: 23 },
      box: [2, 1],
      rate: 1.4,
      v: [-1, 1, 4, 8],
      g: 150,
      drag: 0.9,
      life: [0.3, 0.45],
      size: [2, 2],
      tall: 1.5,
      tones: DRIP_TONES,
      layer: 'gun',
    },
  ],
};

/** The Ooze set (ADR-0160 amended). */
export const OOZE = drawnLater({ head: () => mask(goo), outfit: SUIT, gun: GOOED, flair: FLAIR });
